import React, { useEffect, useId, useState } from 'react';
import { Button, cn } from '../ui';
import type { BhajanFact } from './deriveBhajanDetail';

/**
 * "Did You Know?" card, mirroring the bm app's.
 *
 * Three kinds of fact, always in this order: a vajrabandhi (diamond) note that
 * owns the layout toggle, a torabhandi (acrostic) note, then whatever
 * BHAJAN_FACT nodes the database holds for this bhajan in the current
 * language.
 *
 * The first two are hardcoded because they are derived from the lyrics markup
 * rather than stored; the rest arrive already resolved through
 * deriveBhajanDetail.
 *
 * Expansion follows jnana-native: the card opens itself only for a
 * diamond/acrostic bhajan, where the body holds the layout toggle the reader
 * is likely to want. A plain database fact stays behind one tap so it does not
 * push the lyrics down the page unbidden.
 */

const DIAMOND_FACT =
  'This is a vajrabandhi kriti. The lyrics are arranged in a beautiful diamond (vajra) shape.';
const ACROSTIC_FACT =
  'This is a torabhandi kriti. The highlighted syllables form a meaningful pattern.';

const BulbIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
  </svg>
);

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('shrink-0 text-text-secondary transition-transform', open && 'rotate-180')}
    aria-hidden
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * One row in the card body. Every block looks the same from the outside — a
 * paragraph plus optional extras — so the separator can be applied uniformly
 * by the caller instead of each block re-deriving "am I followed by anything?"
 * from the other flags.
 */
const FactBlock: React.FC<{
  text: string;
  isLast: boolean;
  children?: React.ReactNode;
}> = ({ text, isLast, children }) => (
  <div data-fact-block className={cn('py-3', !isLast && 'border-b border-light')}>
    <p className="text-body leading-relaxed text-text-secondary">{text}</p>
    {children}
  </div>
);

export interface BhajanFactsCardProps {
  hasDiamondSection: boolean;
  hasAcrosticMarkers: boolean;
  showingDiamondView: boolean;
  onToggleDiamondView: () => void;
  /** Database facts for the language on screen. Optional: most bhajans have none. */
  facts?: BhajanFact[];
  className?: string;
}

export const BhajanFactsCard: React.FC<BhajanFactsCardProps> = ({
  hasDiamondSection,
  hasAcrosticMarkers,
  showingDiamondView,
  onToggleDiamondView,
  facts = [],
  className,
}) => {
  const isSpecialBhajan = hasDiamondSection || hasAcrosticMarkers;
  const [isExpanded, setIsExpanded] = useState(isSpecialBhajan);
  const bodyId = useId();

  /**
   * Auto-expand must be an EFFECT, not just the useState seed.
   *
   * BhajanDetails mounts this card unconditionally and the card self-nulls
   * when it has nothing to show — so the component instance exists from the
   * very first render, when `lyrics` is still null and analyzeLyrics('')
   * reports neither a diamond nor an acrostic. Seeding state alone would latch
   * `false` there and never recover once the lyrics land, leaving the diamond
   * toggle hidden behind a click it does not have today.
   *
   * Keyed on the derived boolean, so it fires only on the false -> true
   * transition and never re-opens a card the reader has deliberately closed.
   */
  useEffect(() => {
    if (isSpecialBhajan) setIsExpanded(true);
  }, [isSpecialBhajan]);

  /**
   * The card body as one flat list, in the order jnana-native uses: diamond,
   * then acrostic, then the database facts in edge order.
   *
   * Built as data rather than three conditional JSX branches so the separator
   * rule stays a single statement ("every block but the last") instead of each
   * branch re-deriving whether anything follows it from the other two flags —
   * which is where the old two-branch version was already awkward and would
   * only get worse with a variable-length third kind.
   */
  const blocks: Array<{ key: string; text: string; extra?: React.ReactNode }> = [];
  if (hasDiamondSection) {
    blocks.push({
      key: 'diamond',
      text: DIAMOND_FACT,
      extra: (
        <Button variant="secondary" size="sm" className="mt-3" onClick={onToggleDiamondView}>
          {showingDiamondView ? 'View Normal Lyrics' : 'View Diamond Layout'}
        </Button>
      ),
    });
  }
  if (hasAcrosticMarkers) {
    blocks.push({ key: 'acrostic', text: ACROSTIC_FACT });
  }
  for (const fact of facts) {
    blocks.push({
      key: `fact-${fact.nodeId}`,
      text: fact.text,
      extra: fact.autoTranslated ? (
        /* text-secondary, not text-tertiary: tertiary on bg-4 measures 4.04:1,
           under the 4.5:1 AA floor for text this size. */
        <span className="mt-2 inline-block rounded bg-bg-4 px-2 py-0.5 text-[11px] text-text-secondary">
          Auto-translated
        </span>
      ) : undefined,
    });
  }

  const factCount = blocks.length;
  if (factCount === 0) return null;

  return (
    <div className={cn('max-w-xl overflow-hidden rounded-xl border border-light bg-bg-2', className)}>
      {/* focus-visible:outline-offset-[-2px]: the header fills the card edge to edge and
          the card clips overflow (so the hover fill follows the rounded corners), which
          would clip away the global focus ring — theme.css draws it 2px OUTSIDE the
          button. Insetting it keeps keyboard focus visible. */}
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        aria-controls={bodyId}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition-colors hover:bg-bg-3 focus-visible:outline-offset-[-2px] sm:px-5"
      >
        <span className="flex min-w-0 items-center gap-3">
          {/* bg-4 rather than a gold tint: the accent tokens are bare CSS vars,
              so Tailwind opacity modifiers on them are not reliable. */}
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bg-4 text-accent-gold">
            <BulbIcon />
          </span>
          <span className="font-display text-h3 font-semibold text-accent-gold">Did You Know?</span>
          {factCount > 1 && (
            /* text-secondary, not text-tertiary: #94a3b8 on bg-4 (#334155) is
               4.04:1, below the 4.5:1 AA floor. */
            <span className="rounded-full bg-bg-4 px-2 py-0.5 font-mono text-[11px] tabular-nums text-text-secondary">
              {factCount}
            </span>
          )}
        </span>
        <ChevronIcon open={isExpanded} />
      </button>

      {isExpanded && (
        <div id={bodyId} className="px-4 pb-5 sm:px-5">
          {blocks.map((block, i) => (
            <FactBlock key={block.key} text={block.text} isLast={i === blocks.length - 1}>
              {block.extra}
            </FactBlock>
          ))}
        </div>
      )}
    </div>
  );
};

export default BhajanFactsCard;
